/**
 * StageTransitionPreview - A beautiful, UntitledUI-inspired component for visualizing
 * pipeline stage transitions in proposals.
 *
 * Features:
 * - Visual step indicator showing all stages in the pipeline
 * - Clear distinction between current and proposed stages
 * - Smooth, polished design matching UntitledUI patterns
 * - Responsive and accessible
 */

import { ArrowRight, Check } from '@untitledui/icons';
import { type ReactNode, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';
import type { Stage } from '@/lib/crm/hooks/useStages';
import { cx } from '@/utils/cx';

export interface StageTransitionPreviewProps {
  /** Current stage entity_id */
  currentValue: string | null | undefined;
  /** Proposed stage entity_id */
  proposedValue: string | null | undefined;
  /** CRM entity type to determine which stages to show */
  entityType: 'ahoy_deal' | 'ahoy_contact';
  /** Whether to show the full pipeline visualization */
  showPipeline?: boolean;
  /** Custom class name */
  className?: string;
}

/**
 * StageTransitionPreview - Main component
 */
export function StageTransitionPreview({
  currentValue,
  proposedValue,
  entityType,
  showPipeline = true,
  className,
}: StageTransitionPreviewProps) {
  const { currentOrg } = useOrg();
  const { getStagesForObjectType, getStageByEntityId, isLoading } =
    usePipelineStages(currentOrg?.id, entityType);
  const { pipelines, isLoading: pipelinesLoading } = usePipelines(
    entityType === 'ahoy_deal' ? currentOrg?.id : undefined
  );

  // Get stages for this entity type
  const entityStages = useMemo(() => {
    return getStagesForObjectType(entityType);
  }, [getStagesForObjectType, entityType]);

  // Look up current and proposed stages
  const currentStage = currentValue
    ? getStageByEntityId(currentValue)
    : undefined;
  const proposedStage = proposedValue
    ? getStageByEntityId(proposedValue)
    : undefined;

  // Get stages for the relevant pipeline
  const pipelineStages = useMemo(() => {
    // For contacts, use pipeline_name from the stage (still populated)
    if (entityType === 'ahoy_contact') {
      const pipelineName =
        proposedStage?.pipeline_name || currentStage?.pipeline_name;
      if (!pipelineName) {
        return entityStages;
      }
      return entityStages
        .filter(s => s.pipeline_name === pipelineName)
        .sort((a, b) => a.order - b.order);
    }

    // For deals, use pipelines data to find which pipeline contains the stage
    const stageId = proposedValue || currentValue;
    if (!stageId || pipelines.length === 0) {
      // Graceful degradation: show all stages of the type
      return entityStages;
    }

    // Find pipeline that contains this stage
    const pipeline = pipelines.find(p =>
      p.stages.some(s => s.entity_id === stageId)
    );

    if (!pipeline) {
      // Stage not in any pipeline, show all stages
      return entityStages;
    }

    // Return stages from this pipeline, sorted by order
    return pipeline.stages
      .filter(s => s.entity_id != null)
      .map(s => entityStages.find(ts => ts.entity_id === s.entity_id))
      .filter((s): s is Stage => s !== undefined)
      .sort((a, b) => a.order - b.order);
  }, [
    entityStages,
    currentStage,
    proposedStage,
    entityType,
    currentValue,
    proposedValue,
    pipelines,
  ]);

  // Find indices
  const currentIndex = currentStage
    ? pipelineStages.findIndex(s => s.entity_id === currentStage.entity_id)
    : -1;
  const proposedIndex = proposedStage
    ? pipelineStages.findIndex(s => s.entity_id === proposedStage.entity_id)
    : -1;

  // Labels
  const currentLabel = currentStage?.label || '(not set)';
  const proposedLabel = proposedStage?.label || '(not set)';

  // Determine if forward transition
  const isForward = proposedIndex >= currentIndex;

  // If loading, show placeholder
  if (isLoading || (entityType === 'ahoy_deal' && pipelinesLoading)) {
    return (
      <div className={cx('flex items-center gap-2', className)}>
        <div className='h-8 w-24 bg-bg-secondary animate-pulse rounded-full' />
        <div className='h-8 w-8 bg-bg-secondary animate-pulse rounded-full' />
        <div className='h-8 w-24 bg-bg-secondary animate-pulse rounded-full' />
      </div>
    );
  }

  // If no change, show simplified view
  const hasChange = currentValue !== proposedValue;

  if (!hasChange) {
    return (
      <div className={cx('flex items-center gap-2', className)}>
        <StageBadge label={currentLabel} variant='current' />
        <span className='text-xs text-fg-quaternary italic'>No change</span>
      </div>
    );
  }

  return (
    <div className={cx('space-y-3', className)}>
      {/* Compact transition view */}
      <div className='flex items-center gap-3'>
        <StageBadge label={currentLabel} variant='current' />
        <TransitionArrow isForward={isForward} />
        <StageBadge
          label={proposedLabel}
          variant='proposed'
          isForward={isForward}
        />
      </div>

      {/* Pipeline visualization - show all stages */}
      {showPipeline && pipelineStages.length > 1 && (
        <PipelineVisualization
          stages={pipelineStages}
          currentIndex={currentIndex}
          proposedIndex={proposedIndex}
        />
      )}
    </div>
  );
}

/**
 * Stage Badge - A beautiful pill-style badge for a stage
 */
interface StageBadgeProps {
  label: string;
  variant: 'current' | 'proposed' | 'inactive' | 'passed';
  isForward?: boolean;
}

function StageBadge({ label, variant, isForward = true }: StageBadgeProps) {
  const baseClasses =
    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all';

  const variantClasses = {
    current:
      'bg-utility-gray-100 text-utility-gray-700 ring-1 ring-inset ring-utility-gray-200',
    proposed: isForward
      ? 'bg-utility-brand-50 text-utility-brand-700 ring-1 ring-inset ring-utility-brand-200'
      : 'bg-utility-warning-50 text-utility-warning-700 ring-1 ring-inset ring-utility-warning-200',
    inactive: 'bg-bg-secondary text-fg-quaternary',
    passed:
      'bg-utility-success-50 text-utility-success-700 ring-1 ring-inset ring-utility-success-200',
  };

  return (
    <span className={cx(baseClasses, variantClasses[variant])}>
      {variant === 'passed' && <Check className='w-3.5 h-3.5' />}
      {label}
    </span>
  );
}

/**
 * Transition Arrow - Animated arrow between stages
 */
interface TransitionArrowProps {
  isForward: boolean;
}

function TransitionArrow({ isForward }: TransitionArrowProps) {
  return (
    <div
      className={cx(
        'flex items-center justify-center w-8 h-8 rounded-full',
        isForward
          ? 'bg-utility-brand-50 text-utility-brand-600'
          : 'bg-utility-warning-50 text-utility-warning-600'
      )}
    >
      <ArrowRight className={cx('w-4 h-4', !isForward && 'rotate-180')} />
    </div>
  );
}

/**
 * Pipeline Visualization - Shows all stages with progress
 */
interface PipelineVisualizationProps {
  stages: Stage[];
  currentIndex: number;
  proposedIndex: number;
}

function PipelineVisualization({
  stages,
  currentIndex,
  proposedIndex,
}: PipelineVisualizationProps) {
  return (
    <div className='relative'>
      {/* Progress line background */}
      <div className='absolute top-3.5 left-3 right-3 h-0.5 bg-border-secondary' />

      {/* Progress line fill */}
      {proposedIndex >= 0 && (
        <div
          className='absolute top-3.5 left-3 h-0.5 bg-fg-brand-primary transition-all duration-300'
          style={{
            width: `calc(${(proposedIndex / (stages.length - 1)) * 100}% - 6px)`,
          }}
        />
      )}

      {/* Stage dots and labels */}
      <div className='relative flex justify-between'>
        {stages.map((stage, index) => {
          const isPassed = index < proposedIndex;
          const isCurrent = index === currentIndex;
          const isProposed = index === proposedIndex;
          const isFuture = index > proposedIndex;

          let dotIndicator: ReactNode;
          if (isPassed) {
            dotIndicator = (
              <Check className='w-3.5 h-3.5 text-utility-success-600' />
            );
          } else if (isProposed) {
            dotIndicator = (
              <div className='w-2.5 h-2.5 rounded-full bg-utility-brand-500' />
            );
          } else if (isCurrent) {
            dotIndicator = (
              <div className='w-2 h-2 rounded-full bg-utility-gray-500' />
            );
          } else {
            dotIndicator = (
              <div className='w-2 h-2 rounded-full bg-border-tertiary' />
            );
          }

          return (
            <div
              key={stage.entity_id}
              className='flex flex-col items-center gap-1.5'
            >
              {/* Dot */}
              <div
                className={cx(
                  'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200',
                  isPassed &&
                    'bg-utility-success-100 ring-2 ring-utility-success-500',
                  isProposed &&
                    'bg-utility-brand-100 ring-2 ring-utility-brand-500',
                  isCurrent &&
                    !isProposed &&
                    'bg-utility-gray-100 ring-2 ring-utility-gray-400',
                  isFuture && 'bg-bg-secondary ring-2 ring-border-secondary'
                )}
              >
                {dotIndicator}
              </div>

              {/* Label */}
              <span
                className={cx(
                  'text-xs font-medium text-center max-w-[80px] leading-tight',
                  isPassed && 'text-utility-success-700',
                  isProposed && 'text-utility-brand-700',
                  isCurrent && !isProposed && 'text-fg-secondary',
                  isFuture && 'text-fg-quaternary'
                )}
              >
                {stage.label}
              </span>

              {/* Current indicator */}
              {isCurrent && !isProposed && (
                <span className='text-[10px] text-fg-quaternary uppercase tracking-wide'>
                  Current
                </span>
              )}

              {/* Proposed indicator */}
              {isProposed && (
                <span className='text-[10px] text-utility-brand-600 uppercase tracking-wide font-medium'>
                  Proposed
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
